

import { 
  DatabaseSelectRequest, 
  DatabaseSelectMaybeSingleRequest,
  DatabaseInsertRequest,
  DatabaseInsertSingleRequest,
  DatabaseUpdateRequest,
  DatabaseDeleteRequest
} from "@/types/database";
import { buildNetlifyCallback } from "@/lib/backend";
import { appApi } from "./appApi";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { skipToken } from "@reduxjs/toolkit/query/react";

const doDatabaseSelect = buildNetlifyCallback<DatabaseSelectRequest<any>, any[]>("DatabaseSelect");
const doDatabaseSelectMaybeSingle = buildNetlifyCallback<DatabaseSelectMaybeSingleRequest<any>, any | undefined>("DatabaseSelectMaybeSingle");
const doDatabaseInsertBatch = buildNetlifyCallback<DatabaseInsertRequest<any>, any[]>("DatabaseInsertBatch");
const doDatabaseInsertSingle = buildNetlifyCallback<DatabaseInsertSingleRequest<any>, any>("DatabaseInsertSingle");
const doDatabaseUpdate = buildNetlifyCallback<DatabaseUpdateRequest<any>, any>("DatabaseUpdate");
const doDatabaseDelete = buildNetlifyCallback<DatabaseDeleteRequest<any>, void>("DatabaseDelete");

export const databaseApi = appApi.injectEndpoints({
  endpoints: builder => ({
    databaseSelectBase: builder.query<any[], DatabaseSelectRequest<any>>({
      queryFn: async (request: DatabaseSelectRequest<any>) => {
        return doDatabaseSelect(request);
      },
      providesTags: (result, error, { table }: DatabaseSelectRequest<any>) => {
        return [{ type: 'Database', id: table }];
      },
    }),

    databaseSelectMaybeSingleBase: builder.query<any | undefined, DatabaseSelectMaybeSingleRequest<any>>({
      queryFn: async (request: DatabaseSelectMaybeSingleRequest<any>) => {
        return doDatabaseSelectMaybeSingle(request);
      },
      providesTags: (result, error, { table }: DatabaseSelectMaybeSingleRequest<any>) => {
        return [{ type: 'Database', id: table }];
      },
    }),

    databaseInsertBatchBase: builder.mutation<any[], DatabaseInsertRequest<any>>({
      queryFn: async (request: DatabaseInsertRequest<any>) => {
        return doDatabaseInsertBatch(request);
      },
      invalidatesTags: (result, error, { table }: DatabaseInsertRequest<any>) => {
        return [{ type: 'Database', id: table }];
      },
    }),

    databaseInsertSingleBase: builder.mutation<any, DatabaseInsertSingleRequest<any>>({
      queryFn: async (request: DatabaseInsertSingleRequest<any>) => {
        return doDatabaseInsertSingle(request);
      },
      invalidatesTags: (result, error, { table }: DatabaseInsertSingleRequest<any>) => {
        return [{ type: 'Database', id: table }];
      },
    }),

    databaseUpdateBase: builder.mutation<any, DatabaseUpdateRequest<any>>({
      queryFn: async (request: DatabaseUpdateRequest<any>) => {
        return doDatabaseUpdate(request);
      },
      invalidatesTags: (result, error, { table }: DatabaseUpdateRequest<any>) => {
        return [{ type: 'Database', id: table }];
      },
    }),

    databaseDeleteBase: builder.mutation<void, DatabaseDeleteRequest<any>>({
      queryFn: async (request: DatabaseDeleteRequest<any>) => {
        return doDatabaseDelete(request);
      },
      invalidatesTags: (result, error, { table }: DatabaseDeleteRequest<any>) => {
        return [{ type: 'Database', id: table }];
      },
    }),
  }),
});

const { 
  useDatabaseSelectBaseQuery,
  useDatabaseSelectMaybeSingleBaseQuery,
  useDatabaseInsertBatchBaseMutation,
  useDatabaseInsertSingleBaseMutation,
  useDatabaseUpdateBaseMutation,
  useDatabaseDeleteBaseMutation,
} = databaseApi;

interface DatabaseSelectResult<RowType> {
  data?: RowType[];
  error?: FetchBaseQueryError | SerializedError;
  isLoading: boolean;
}

export function useDatabaseSelect<RowType>(
  request: DatabaseSelectRequest<RowType> | undefined
): DatabaseSelectResult<RowType> {
  const result = useDatabaseSelectBaseQuery(request || skipToken);

  return {
    data: result.data,
    error: result.error,
    isLoading: result.isLoading,
  };
}

interface DatabaseSelectMaybeSingleResult<RowType> {
  data?: RowType;
  error?: FetchBaseQueryError | SerializedError;
  isLoading: boolean;
}

export function useDatabaseSelectMaybeSingle<RowType>(
  request: DatabaseSelectMaybeSingleRequest<RowType> | undefined
): DatabaseSelectMaybeSingleResult<RowType> {
  const result = useDatabaseSelectMaybeSingleBaseQuery(request || skipToken);
  return {
    data: result.data,
    error: result.error,
    isLoading: result.isLoading,
  };
}

interface DatabaseInsertBatchResult<RowType> {
  data?: RowType[];
  error?: FetchBaseQueryError | SerializedError;
  isLoading: boolean;
  insertBatch: (request: DatabaseInsertRequest<RowType>) => Promise<{ data?: RowType[]; error?: FetchBaseQueryError | SerializedError }>;
}

export function useDatabaseInsertBatch<RowType>(): DatabaseInsertBatchResult<RowType> {
  const [insertBatch, result] = useDatabaseInsertBatchBaseMutation();
  
  return {
    data: result.data,
    error: result.error,
    isLoading: result.isLoading,
    insertBatch,
  };
}

interface DatabaseInsertSingleResult<RowType> {
  data?: RowType;
  error?: FetchBaseQueryError | SerializedError;
  isLoading: boolean;
  insertSingle: (request: DatabaseInsertSingleRequest<RowType>) => Promise<{ data?: RowType; error?: FetchBaseQueryError | SerializedError }>;
}

export function useDatabaseInsertSingle<RowType>(): DatabaseInsertSingleResult<RowType> {
  const [insertSingle, result] = useDatabaseInsertSingleBaseMutation();

  return {
    data: result.data,
    error: result.error,
    isLoading: result.isLoading,
    insertSingle,
  };
}

interface DatabaseUpdateResult<RowType> {
  data?: RowType;
  error?: FetchBaseQueryError | SerializedError;
  isLoading: boolean;
  update: (request: DatabaseUpdateRequest<RowType>) => Promise<{ data?: RowType; error?: FetchBaseQueryError | SerializedError }>;
}

export function useDatabaseUpdate<RowType>(): DatabaseUpdateResult<RowType> {
  const [update, result] = useDatabaseUpdateBaseMutation();

  return {
    data: result.data,
    error: result.error,
    isLoading: result.isLoading,
    update,
  };
}

interface DatabaseDeleteResult {
  error?: FetchBaseQueryError | SerializedError;
  isLoading: boolean;
  deleteRows: (request: DatabaseDeleteRequest<any>) => Promise<{ error?: FetchBaseQueryError | SerializedError }>;
}

export function useDatabaseDelete(): DatabaseDeleteResult {
  const [deleteRows, result] = useDatabaseDeleteBaseMutation();

  return {
    error: result.error,
    isLoading: result.isLoading,
    deleteRows,
  };
}
